import * as THREE from 'three';

// H36M 17-joint names (must match backend/app/services/joint_mapping.py lines 47-65)
export const H36M_JOINT_NAMES = [
  'hip_center', 'right_hip', 'right_knee', 'right_ankle',
  'left_hip', 'left_knee', 'left_ankle',
  'spine', 'neck', 'head', 'head_top',
  'left_shoulder', 'left_elbow', 'left_wrist',
  'right_shoulder', 'right_elbow', 'right_wrist',
];

// Parent index for each joint (-1 = root)
export const H36M_PARENT_INDICES = [-1, 0, 1, 2, 0, 4, 5, 0, 7, 8, 9, 8, 11, 12, 8, 14, 15];

// 16 bone pairs: [childIdx, parentIdx]
export const H36M_BONE_PAIRS: [number, number][] = H36M_PARENT_INDICES
  .map((parent, child) => [child, parent] as [number, number])
  .filter(([_, parent]) => parent >= 0);

// Left/right index swap for mirroring
const LEFT_RIGHT_SWAP: Record<number, number> = {
  1: 4, 4: 1, 2: 5, 5: 2, 3: 6, 6: 3,
  11: 14, 14: 11, 12: 15, 15: 12, 13: 16, 16: 13,
};

// Which body side each bone belongs to (for color mapping fallback)
export const BONE_SIDE: Record<number, 'left' | 'right' | 'center'> = {};
// Bones 1-3 = right leg, 4-6 = left leg, 7-10 = spine/head, 11-13 = left arm, 14-16 = right arm
H36M_BONE_PAIRS.forEach(([child, parent], idx) => {
  if ([1, 2, 3].includes(child) || [1, 2, 3].includes(parent)) BONE_SIDE[idx] = 'right';
  else if ([4, 5, 6].includes(child) || [4, 5, 6].includes(parent)) BONE_SIDE[idx] = 'left';
  else if ([11, 12, 13].includes(child)) BONE_SIDE[idx] = 'left';
  else if ([14, 15, 16].includes(child)) BONE_SIDE[idx] = 'right';
  else BONE_SIDE[idx] = 'center';
});

/**
 * Map a 0-100 score to a color:
 * 90+ = green (#22c55e), 70-90 = gradient to yellow (#eab308), <70 = gradient to red (#ef4444)
 */
export function scoreToColor(score: number): string {
  if (score >= 90) return '#22c55e';
  if (score >= 70) {
    const t = (score - 70) / 20; // 0 at 70, 1 at 90
    return lerpColor('#eab308', '#22c55e', t);
  }
  const t = Math.max(0, score) / 70; // 0 at 0, 1 at 70
  return lerpColor('#ef4444', '#eab308', t);
}

function lerpColor(a: string, b: string, t: number): string {
  const ca = new THREE.Color(a);
  const cb = new THREE.Color(b);
  ca.lerp(cb, t);
  return '#' + ca.getHexString();
}

/**
 * Mirror H36M joints for mirrored evaluations:
 * - Negate X coordinate
 * - Swap left/right joint indices
 */
export function mirrorH36MJoints(joints: number[][]): number[][] {
  const mirrored = new Array(joints.length);
  for (let i = 0; i < joints.length; i++) {
    const swapTo = LEFT_RIGHT_SWAP[i];
    const src = swapTo !== undefined ? joints[swapTo] : joints[i];
    mirrored[i] = [-src[0], src[1], src[2]];
  }
  return mirrored;
}

/**
 * Map ComponentDetail labels to bone indices for color coding.
 * Returns a Map<boneIdx, score>.
 */
export function mapComponentDetailsToBoneScores(
  details: Array<{ label: string; type: string; avg_score: number }> | undefined
): Map<number, number> | undefined {
  if (!details || details.length === 0) return undefined;

  const scores = new Map<number, number>();

  // Map region labels to bone indices
  // BDS labels from scoring.py BONE_REGIONS:
  // "Left Arm" -> bones with left arm indices (child 12,13 = left elbow, left wrist)
  // "Right Arm" -> bones with right arm indices (child 15,16)
  // "Left Leg" -> bones with left leg indices (child 5,6)
  // "Right Leg" -> bones with right leg indices (child 2,3)
  // "Torso" -> spine bones
  const labelToSide: Record<string, 'left' | 'right' | 'center'> = {
    'Left Arm': 'left', 'Right Arm': 'right',
    'Left Leg': 'left', 'Right Leg': 'right',
    'Torso': 'center',
  };

  for (const detail of details) {
    if (detail.type !== 'bds') continue;
    const side = labelToSide[detail.label];
    if (!side) continue;

    H36M_BONE_PAIRS.forEach(([_child, _parent], boneIdx) => {
      if (BONE_SIDE[boneIdx] === side) {
        scores.set(boneIdx, detail.avg_score);
      }
    });
  }

  return scores.size > 0 ? scores : undefined;
}
